import { useEffect, useState } from 'react'
import { disablePush, enablePush, pushState, type PushState } from '../lib/push'
import { isInstalled, isIos } from '../lib/pwa'

/**
 * The nag switch.
 *
 * Framed around the only failure that actually kills a league: forgetting.
 * A missed week is a zero, and a zero in week 3 is usually the last week
 * somebody plays.
 */
export default function NotifyToggle() {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => { void pushState().then(setState) }, [])

  const toggle = async () => {
    setBusy(true); setErr(null)
    try {
      setState(state === 'on' ? await disablePush() : await enablePush())
    } catch (e) {
      setErr((e as Error).message)
    }
    setBusy(false)
  }

  if (state === null) return null

  // iOS only exposes push to a home-screen install, so the honest move on an
  // iPhone in Safari is the install hint, not a button that cannot work.
  if (state === 'unsupported' && isIos() && !isInstalled()) {
    return (
      <section className="card">
        <h2>Pick reminders</h2>
        <p className="muted">
          Add Gridlock to your home screen first — Share, then <strong>Add to Home
          Screen</strong>. iPhone only allows reminders from the installed app.
        </p>
      </section>
    )
  }

  return (
    <section className="card">
      <div className="row between">
        <div>
          <h2>Pick reminders</h2>
          <p className="muted">
            {state === 'on'
              ? 'On. A nudge the day before, and a last call 90 minutes before kickoff.'
              : 'A nudge the day before kickoff, and a last call 90 minutes out. Nothing else.'}
          </p>
        </div>
        {state !== 'unsupported' && state !== 'unconfigured' && state !== 'denied' && (
          <button className={state === 'on' ? 'btn' : 'btn btn-primary'}
                  disabled={busy} onClick={toggle}>
            {busy ? '…' : state === 'on' ? 'Turn off' : 'Turn on'}
          </button>
        )}
      </div>

      {state === 'denied' && (
        <p className="muted">
          Notifications are blocked for this site. Turn them back on in your browser's
          site settings, then reload.
        </p>
      )}
      {state === 'unsupported' && (
        <p className="muted">This browser doesn't support push notifications.</p>
      )}
      {state === 'unconfigured' && (
        <p className="muted">Reminders aren't switched on for this deployment yet.</p>
      )}
      {err && <p className="error">{err}</p>}
    </section>
  )
}
